import { getDefaultMobileConfig } from "@solana/connector/headless";
import { AppProvider, getDefaultConfig } from "@solana/connector/react";
import { APP_NAME, RPC_ENDPOINT } from "@solomon-apps/config";
import React, { useMemo, useState } from "react";

import { SolomonE2EWalletProvider } from "./e2e";
import { isMobileWalletAdapterSupportedEnvironment } from "./mobileWalletAdapter";
import { WalletModalProvider } from "./WalletModal";

interface WalletProviderProps {
    children: React.ReactNode;
    rpcEndpoint?: typeof RPC_ENDPOINT;
    network?: "mainnet-beta" | "devnet" | "testnet";
}

/**
 * WalletProvider
 *
 * Root provider for wallet connectivity using Solana Foundation's ConnectorKit.
 * Provides:
 * - AppProvider from ConnectorKit for wallet connection management
 * - WalletModalProvider for programmatic modal control
 * - Mobile Wallet Adapter (MWA) for native Android wallet signing
 *
 * ConnectorKit automatically detects Wallet Standard compliant wallets
 * (Phantom, Solflare, Backpack, etc.) - no manual adapter list needed.
 *
 * Note: ConnectionManager is initialized in createApp() before React renders.
 */
export function WalletProvider({
    children,
    rpcEndpoint = RPC_ENDPOINT,
    network = "mainnet-beta"
}: WalletProviderProps): React.ReactElement {
    // Get app URL for ConnectorKit config
    const appUrl =
        typeof window !== "undefined"
            ? window.location.origin
            : "http://localhost:3000";
    const [enableMobileWalletAdapter] = useState(() =>
        isMobileWalletAdapterSupportedEnvironment()
    );

    // ConnectorKit configuration with built-in auto-reconnection
    const connectorConfig = useMemo(
        () =>
            getDefaultConfig({
                appName: APP_NAME,
                appUrl,
                autoConnect: true,
                enableMobile: enableMobileWalletAdapter,
                clusters: [
                    {
                        id:
                            network === "devnet"
                                ? ("solana:devnet" as const)
                                : network === "testnet"
                                  ? ("solana:testnet" as const)
                                  : ("solana:mainnet" as const),
                        label: network === "mainnet-beta" ? "Mainnet" : network,
                        url: rpcEndpoint
                    }
                ]
            }),
        [appUrl, enableMobileWalletAdapter, rpcEndpoint, network]
    );

    // Mobile Wallet Adapter configuration for native Android wallet signing.
    // Registers MWA via @solana-mobile/wallet-standard-mobile, enabling
    // bottom-sheet signing on Solana Mobile devices (Saga/Seeker).
    const mobile = useMemo(() => {
        if (!enableMobileWalletAdapter) {
            return undefined;
        }

        return getDefaultMobileConfig({
            appName: APP_NAME,
            appUrl
        });
    }, [appUrl, enableMobileWalletAdapter]);

    return (
        <AppProvider connectorConfig={connectorConfig} mobile={mobile}>
            <SolomonE2EWalletProvider>
                <WalletModalProvider>{children}</WalletModalProvider>
            </SolomonE2EWalletProvider>
        </AppProvider>
    );
}
